import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import {
	API_ENDPOINTS,
	Badge,
	BottomAnchoredControls,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	CardPlaceholder,
	customToast,
	Dropdown,
	FormInput,
	Icons,
	Modal,
	PageLayout,
	Permissions,
	replaceKeyWithValue,
	route,
	StringHelpers,
	SubTitle,
	useAuthorisationContext,
	useLangContext,
	useDisabledContext,
	Variants,
	WidthConstrainedContainer,
} from 'carrier-fe';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';
import {
	Appliance,
	ApplianceAttributes,
	ApplianceEditData,
	ApplianceModalType,
} from '../../../types/appliance-management';
import { SEARCH_FILTERS } from '../../../Constants';
import SerialPrefixesSection from '../components/SerialPrefixesSection';
import WarrantyOptionsSection from '../components/WarrantyOptionsSection';
import AuditLogSection from '../components/AuditLogSection';
import ImportWarrantyModal from '../components/ImportWarrantyModal';

const LIST_PATH = '/appliance-management';

const emptyDetails = {
	brand: '',
	appliance_type: '',
	name: '',
};

type Details = typeof emptyDetails;

/**
 * CUS-934 - the appliance detail page, serving create, read-only view and edit.
 *
 * Create is details-only. There is no appliance id to attach a serial prefix or a warranty option to until
 * the appliance exists, and the API has no bulk endpoint, so the child sections appear once there is
 * something to hang them off. On success this redirects to the edit page, where they are live.
 *
 * The three GETs are not interchangeable. `show` embeds the children; `edit` returns the editable fields
 * plus the certificate template lengths and the rename cost, and authorises `update`, so a view-only user
 * must never call it; and the select endpoint supplies the option lists, which `edit` cannot provide on
 * create because there is no id to call it against.
 */
function ApplianceForm() {
	const { applianceId } = useParams();
	const navigate = useNavigate();
	const { crud, fields } = useLangContext();
	const { disabled, setDisabled } = useDisabledContext();
	const { hasPermissionAccess } = useAuthorisationContext();

	const isCreate = !applianceId;
	const isEdit = !!applianceId && window.location.pathname.endsWith('/edit');
	const isReadOnly = !!applianceId && !isEdit;

	const [appliance, setAppliance] = useState<Appliance | null>(null);
	const [editData, setEditData] = useState<ApplianceEditData | null>(null);
	const [attributes, setAttributes] = useState<ApplianceAttributes | null>(
		null
	);
	const [details, setDetails] = useState<Details>(emptyDetails);
	const [errors, setErrors] = useState<Record<string, string[]>>({});
	const [isFetching, setIsFetching] = useState<boolean>(true);
	const [renameConfirmOpen, setRenameConfirmOpen] = useState<boolean>(false);
	const [auditRefetchKey, setAuditRefetchKey] = useState<number>(0);
	const [activeModal, setActiveModal] = useState<ApplianceModalType>(null);

	const modelName = crud?.models?.viessmann_appliance || 'Appliance';

	const title = useMemo(() => {
		const template = isCreate
			? crud?.pages?.create?.title || 'Create :model'
			: isEdit
			? crud?.pages?.edit?.title || 'Edit :model'
			: crud?.pages?.view?.title || 'View :model';

		return StringHelpers.title(
			replaceKeyWithValue(template, 'model', modelName)
		);
	}, [crud, isCreate, isEdit, modelName]);

	useEffect(() => {
		load();
	}, [applianceId, isEdit]);

	const load = async () => {
		setIsFetching(true);

		try {
			const requests: Promise<any>[] = [];

			if (applianceId) {
				requests.push(
					axios.get(
						route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.VIEW, {
							viessmannAppliance: applianceId,
						})
					)
				);
			}

			/*
			 * Only when the form is editable. The select endpoint authorises viewAny and `edit` authorises
			 * update, so asking for either on a read-only page would 403 a user who legitimately has only
			 * view rights.
			 */
			if (!isReadOnly) {
				requests.push(
					axios.get(
						route(
							API_ENDPOINTS.GENERIC.SELECT
								.VIESSMANN_APPLIANCE_ATTRIBUTES
						)
					)
				);
			}

			if (isEdit && applianceId) {
				requests.push(
					axios.get(
						route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.EDIT, {
							viessmannAppliance: applianceId,
						})
					)
				);
			}

			const responses = await Promise.all(requests);
			let cursor = 0;

			if (applianceId) {
				const shown: Appliance = responses[cursor++].data?.data;
				setAppliance(shown);
				setDetails({
					brand: shown?.brand ?? '',
					appliance_type: shown?.appliance_type ?? '',
					name: shown?.name ?? '',
				});
			}

			if (!isReadOnly) {
				setAttributes(responses[cursor++].data?.data ?? null);
			}

			if (isEdit && applianceId) {
				setEditData(responses[cursor++].data?.data ?? null);
			}
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setIsFetching(false);
		}
	};

	/** Re-reads the children after a prefix or warranty change, and refreshes the audit log with them. */
	const reloadChildren = async () => {
		if (!applianceId) return;

		try {
			const { data } = await axios.get(
				route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.VIEW, {
					viessmannAppliance: applianceId,
				})
			);

			setAppliance(data?.data);
			setAuditRefetchKey((key) => key + 1);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error refreshing the appliance.',
				variant: Variants.Danger,
			});
		}
	};

	/*
	 * The actions that act on the appliance as a whole. They live here rather than on the listing row
	 * because each one is consequential enough to want the appliance in front of you first: archiving
	 * changes what the catalogue offers, and importing warranty options replaces the whole set.
	 *
	 * Driven by the row's own permissions object rather than the account's global permissions, because the
	 * policy also encodes state - update and delete are false once archived, restore is false until then -
	 * so this cannot offer an action the API would refuse.
	 */
	const detailActions = useMemo((): DropdownItemProps[] => {
		if (!appliance || !appliance.permissions) return [];

		const actions: DropdownItemProps[] = [];

		if (appliance.permissions.update) {
			actions.push({
				label: crud?.select_labels?.edit || 'Edit',
				icon: Icons.EDIT,
				onClick: () => navigate(`${LIST_PATH}/${appliance.id}/edit`),
			});
		}

		if (
			appliance.permissions.update &&
			hasPermissionAccess(
				Permissions.VIESSMANN_ADMIN_APPLIANCE_WARRANTY_STORE
			)
		) {
			actions.push({
				label:
					crud?.select_labels?.import_warranty || 'Import Warranty',
				icon: Icons.COPY,
				onClick: () => setActiveModal('import_warranty'),
			});
		}

		if (appliance.permissions.delete) {
			actions.push({
				label: crud?.select_labels?.archive || 'Archive',
				icon: Icons.DELETE,
				variant: Variants.Danger,
				onClick: () => setActiveModal('archive'),
			});
		}

		/*
		 * Restore is offered here because the listing row only offers View. CUS-933 puts it in the listing
		 * behind the View Archived checkbox; View Archived now leads here instead, which is the only place
		 * an archived appliance can be acted on.
		 */
		if (appliance.permissions.restore) {
			actions.push({
				label: crud?.select_labels?.restore || 'Restore',
				// No restore icon in the set; RESUME is the nearest semantic.
				icon: Icons.RESUME,
				onClick: () => setActiveModal('restore'),
			});
		}

		return actions;
	}, [appliance, crud, hasPermissionAccess, navigate]);

	const archive = async () => {
		if (!applianceId) return;

		setDisabled(true);

		try {
			const { data } = await axios.delete(
				route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.DELETE, {
					viessmannAppliance: applianceId,
				})
			);

			customToast({
				title: data?.message || 'Appliance archived.',
				variant: Variants.Success,
			});

			/*
			 * Stay on the page rather than returning to the listing. The appliance is now hidden from the
			 * listing, so going back would look like it had been deleted - and Restore is here.
			 */
			await load();
			setAuditRefetchKey((key) => key + 1);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error archiving the appliance.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			setActiveModal(null);
		}
	};

	const restore = async () => {
		if (!applianceId) return;

		setDisabled(true);

		try {
			const { data } = await axios.patch(
				route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.RESTORE, {
					viessmannAppliance: applianceId,
				})
			);

			customToast({
				title: data?.message || 'Appliance restored.',
				variant: Variants.Success,
			});

			await load();
			setAuditRefetchKey((key) => key + 1);
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'Error restoring the appliance.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			setActiveModal(null);
		}
	};

	/*
	 * How many installations a rename would cost. Those whose appliance link is still null resolve their
	 * warranty options by matching this name exactly, so renaming takes those options away from them -
	 * immediately, retroactively, and with nothing but a log line to show for it. Correcting a wrong name is
	 * a legitimate thing to do, so this confirms rather than blocks.
	 */
	const isRenaming =
		isEdit && !!appliance && details.name.trim() !== appliance.name;
	const renameCost = editData?.unlinked_installations_count ?? 0;

	const submit = async () => {
		if (isRenaming && renameCost > 0 && !renameConfirmOpen) {
			setRenameConfirmOpen(true);
			return;
		}

		setRenameConfirmOpen(false);
		setDisabled(true);
		setErrors({});

		try {
			const { data } = applianceId
				? await axios.patch(
						route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.UPDATE, {
							viessmannAppliance: applianceId,
						}),
						details
				  )
				: await axios.post(
						route(API_ENDPOINTS.ADMIN.VIESSMANN_APPLIANCE.STORE),
						details
				  );

			customToast({
				title: data?.message || 'Appliance saved.',
				variant: Variants.Success,
			});

			if (applianceId) {
				await load();
				setAuditRefetchKey((key) => key + 1);
				return;
			}

			/*
			 * Straight to the edit page rather than back to the listing: the prefixes and warranty options
			 * still have to be added, and this is the first moment there is an id to add them against.
			 */
			navigate(`${LIST_PATH}/${data?.data?.id}/edit`);
		} catch (err: any) {
			setErrors(err?.response?.data?.data?.errors ?? {});

			customToast({
				title:
					err?.response?.data?.message ||
					'Error saving the appliance.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	const brandOptions = attributes?.brands ?? [];
	const applianceTypeOptions = attributes?.appliance_types ?? [];

	const detailsCard = (
		<Card className="mb-4">
			<CardBody className="pb-0">
				<div className="d-flex justify-content-between align-items-start mb-3">
					<div className="d-flex align-items-center gap-2">
						<SubTitle
							title={StringHelpers.title(
								crud?.sub_titles?.details || 'Details'
							)}
							style={{ color: '#464C5E' }}
						/>
						{!!appliance?.deleted_at && (
							<Badge
								label={StringHelpers.title(
									fields?.archived || 'Archived'
								)}
								variant={Variants.Warning}
							/>
						)}
					</div>
					{/*
					 * The appliance-level actions. On the read-only page this is the only way to reach edit,
					 * archive, restore and import warranty, which is deliberate - the listing row offers
					 * View alone.
					 */}
					{isReadOnly && detailActions.length > 0 && (
						<Dropdown
							label={StringHelpers.title(
								crud?.buttons?.actions?.default || 'Actions'
							)}
							items={detailActions}
							type={ButtonTypes.Solid}
							variant={Variants.Dark}
						/>
					)}
				</div>

				{!!appliance?.deleted_at && (
					/*
					 * Says what archiving actually did. Both the serial lookup and the warranty lookup read
					 * archived rows on purpose, so an admin must not read "archived" as "withdrawn".
					 */
					<p className="text-muted">
						{fields?.archived_appliance_note ||
							'This appliance is hidden from the appliance list and cannot be chosen when copying warranty options. Installations already registered against it keep their warranty options, and its serial prefixes are still recognised when an installer looks up a serial number.'}
					</p>
				)}

				{isReadOnly ? (
					<dl className="row mb-4">
						<dt className="col-sm-4">
							{StringHelpers.title(fields?.brand || 'Brand')}
						</dt>
						<dd className="col-sm-8">
							{appliance?.brand_display || '-'}
						</dd>
						<dt className="col-sm-4">
							{StringHelpers.title(
								fields?.appliance_type || 'Appliance Type'
							)}
						</dt>
						<dd className="col-sm-8">
							{appliance?.appliance_type_display || '-'}
						</dd>
						<dt className="col-sm-4">
							{StringHelpers.title(
								fields?.appliance_name || 'Appliance Name'
							)}
						</dt>
						<dd className="col-sm-8">{appliance?.name || '-'}</dd>
						{/*
						 * Which row of the legacy warranty matrix produced this appliance's options. It is
						 * the only field that answers that during cutover, and it goes when the sheet does.
						 */}
						{!!appliance?.warranty_matrix_name && (
							<>
								<dt className="col-sm-4">
									{StringHelpers.title(
										fields?.warranty_matrix_name ||
											'Warranty matrix row'
									)}
								</dt>
								<dd className="col-sm-8 mb-0">
									{appliance.warranty_matrix_name}
								</dd>
							</>
						)}
					</dl>
				) : (
					<>
						<FormInput
							type="select"
							name="brand"
							label={StringHelpers.title(fields?.brand || 'brand')}
							placeholder={StringHelpers.title(
								fields?.please_select || 'please select'
							)}
							value={details.brand}
							onChange={(value) =>
								setDetails((prev) => ({
									...prev,
									brand: value as string,
								}))
							}
							options={brandOptions}
							disabled={disabled}
							errorMessages={errors?.brand}
						/>

						<FormInput
							type="select"
							name="appliance_type"
							label={StringHelpers.title(
								fields?.appliance_type || 'appliance type'
							)}
							placeholder={StringHelpers.title(
								fields?.please_select || 'please select'
							)}
							value={details.appliance_type}
							onChange={(value) =>
								setDetails((prev) => ({
									...prev,
									appliance_type: value as string,
								}))
							}
							options={applianceTypeOptions}
							disabled={disabled}
							errorMessages={errors?.appliance_type}
						/>

						<FormInput
							type="text"
							name="name"
							label={StringHelpers.title(
								fields?.appliance_name || 'appliance name'
							)}
							description={
								fields?.appliance_name_description ||
								'Must be unique. This name is how installations already registered find their warranty options, so change it only to correct a genuine mistake.'
							}
							value={details.name}
							onChange={(value) =>
								setDetails((prev) => ({
									...prev,
									name: value as string,
								}))
							}
							disabled={disabled}
							errorMessages={errors?.name}
						/>

						{isRenaming && renameCost > 0 && (
							<p className="text-warning">
								{replaceKeyWithValue(
									fields?.rename_appliance_warning ||
										'Renaming this appliance will remove the warranty options of :count that are still matched to it by name.',
									'count',
									`${renameCost} ${
										renameCost === 1
											? 'installation'
											: 'installations'
									}`
								)}
							</p>
						)}
					</>
				)}
			</CardBody>
		</Card>
	);

	return (
		<PageLayout title={title}>
			<WidthConstrainedContainer>
				{isFetching ? (
					<>
						<CardPlaceholder />
						<CardPlaceholder />
					</>
				) : (
					<>
						{detailsCard}

						{isCreate ? (
							<Card className="mb-4">
								<CardBody>
									<p className="mb-0 text-muted">
										{fields?.save_appliance_first ||
											'Save the appliance to add its serial prefixes and warranty options.'}
									</p>
								</CardBody>
							</Card>
						) : (
							appliance && (
								<>
									<SerialPrefixesSection
										applianceId={appliance.id}
										variants={appliance.variants ?? []}
										attributes={attributes}
										canEdit={isEdit}
										onChanged={reloadChildren}
									/>

									<WarrantyOptionsSection
										applianceId={appliance.id}
										applianceName={appliance.name}
										warranties={appliance.warranties ?? []}
										attributes={attributes}
										certificateTemplateLengths={
											editData?.certificate_template_lengths ??
											[]
										}
										canEdit={isEdit}
										onChanged={reloadChildren}
									/>

									<AuditLogSection
										applianceId={appliance.id}
										refetchKey={auditRefetchKey}
									/>
								</>
							)
						)}
					</>
				)}
			</WidthConstrainedContainer>

			<BottomAnchoredControls>
				<Button
					label={crud?.buttons?.back?.default || 'Back'}
					onClick={() => {
						if (isEdit) return navigate(`${LIST_PATH}/${applianceId}`);

						/*
						 * Create never came from a row, so it must not consume a query string stashed by
						 * an earlier visit to some other appliance.
						 */
						if (isCreate) return navigate(LIST_PATH);

						/*
						 * Back from the read-only view returns the admin to the listing as they left it -
						 * search, filters, archived, page and sort. Already encoded by toString(), so it
						 * goes back on the URL as-is.
						 */
						const saved = sessionStorage.getItem(
							SEARCH_FILTERS.APPLIANCE
						);

						sessionStorage.removeItem(SEARCH_FILTERS.APPLIANCE);

						navigate(saved ? `${LIST_PATH}?${saved}` : LIST_PATH);
					}}
					variant={Variants.Dark}
					type={ButtonTypes.Outline}
					className="me-4"
					disabled={disabled}
				/>
				{!isReadOnly && (
					<Button
						label={
							disabled
								? isCreate
									? crud?.buttons?.create?.submitting ||
									  'Creating...'
									: crud?.buttons?.edit?.submitting ||
									  'Updating...'
								: isCreate
								? crud?.buttons?.create?.default || 'Create'
								: crud?.buttons?.edit?.default || 'Update'
						}
						onClick={submit}
						variant={Variants.Primary}
						disabled={
							disabled ||
							isFetching ||
							!details.brand ||
							!details.appliance_type ||
							!details.name
						}
					/>
				)}
			</BottomAnchoredControls>

			<Modal
				open={renameConfirmOpen}
				onClose={() => setRenameConfirmOpen(false)}
				title={StringHelpers.title(
					fields?.rename_appliance_title || 'Rename this appliance?'
				)}
				confirmText={
					disabled
						? crud?.buttons?.edit?.submitting || 'Updating...'
						: crud?.buttons?.edit?.default || 'Update'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={submit}
				disabled={disabled}
				variant={Variants.Danger}
			>
				<p>
					{replaceKeyWithValue(
						replaceKeyWithValue(
							fields?.rename_appliance_confirm ||
								'You are renaming :from to :to.',
							'from',
							appliance?.name || ''
						),
						'to',
						details.name
					)}
				</p>
				<p className="fw-bold text-danger">
					{replaceKeyWithValue(
						fields?.rename_appliance_cost ||
							':count are still matched to this appliance by name. Renaming it takes their warranty options away immediately, and they will not be able to register or extend a warranty.',
						'count',
						`${renameCost} ${
							renameCost === 1 ? 'installation' : 'installations'
						}`
					)}
				</p>
				<p className="mb-0 text-muted">
					{fields?.rename_appliance_note ||
						'Warranties already issued are unaffected. Only the ability to obtain a new or extended one is lost.'}
				</p>
			</Modal>

			<Modal
				open={activeModal === 'archive'}
				onClose={() => setActiveModal(null)}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.archive?.title || 'Archive :name',
						'name',
						modelName
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.archive?.submitting || 'Archiving...'
						: crud?.buttons?.archive?.default || 'Archive'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={archive}
				disabled={disabled}
				variant={Variants.Primary}
			>
				<p>
					{replaceKeyWithValue(
						fields?.archive_appliance_confirm ||
							'Archive :name? It will no longer appear in the appliance list, and it cannot be chosen when copying warranty options.',
						'name',
						appliance?.name || ''
					)}
				</p>
				{/*
				 * This wording is load-bearing. Archiving hides the appliance from the listing and nothing
				 * else: both the serial lookup and the warranty lookup read archived rows on purpose, so
				 * that archiving cannot retrospectively take warranty options away from installations
				 * already registered. An admin must not be left thinking it withdraws anything.
				 */}
				<p className="mb-0 text-muted">
					{fields?.archive_appliance_note ||
						'Installations already registered against it keep their warranty options, and its serial prefixes are still recognised when an installer looks up a serial number.'}
				</p>
			</Modal>

			<Modal
				open={activeModal === 'restore'}
				onClose={() => setActiveModal(null)}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.restore?.title || 'Restore :name',
						'name',
						modelName
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.restore?.submitting || 'Restoring...'
						: crud?.buttons?.restore?.default || 'Restore'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={restore}
				disabled={disabled}
				variant={Variants.Primary}
			>
				<p className="mb-0">
					{replaceKeyWithValue(
						fields?.restore_appliance_confirm ||
							'Restore :name so it appears in the appliance list again?',
						'name',
						appliance?.name || ''
					)}
				</p>
			</Modal>

			<ImportWarrantyModal
				open={activeModal === 'import_warranty'}
				onClose={() => setActiveModal(null)}
				applianceId={appliance?.id ?? null}
				applianceName={appliance?.name ?? null}
				currentWarrantyCount={appliance?.warranties_count ?? 0}
				onCopied={reloadChildren}
			/>
		</PageLayout>
	);
}

export default ApplianceForm;
